'use client';

import Link from 'next/link';
import {
    getStoreDisplayTime,
    storeIsOpen,
    usePageLayout,
} from '@libs/common-next';
import {
    constructServiceURL,
    serviceImages,
    servicesToDisplay,
} from '@app/hcp/utils/storeDetailsServicesModule';
import { Icon, TextPassage, Image } from '@petsmart-ui/sparky';
import type { StoreItemProps } from '@app/hcp/components/StoreDirectory';
import { handleServiceClick } from '@libs/experience-api';

import './StoreDetailsServicesModule.scss';

export function StoreDetailsServicesModule({ store }: StoreItemProps) {
    const { isAuthenticated } = usePageLayout();

    const timezonedDate = new Date().toLocaleString('en', {
        timeZone: store.TimeZone?.TimeZoneId,
    });
    const currentDate = new Date(timezonedDate);
    const sortedServices = store.StoreServices.filter(({ ServiceName }) =>
        servicesToDisplay.includes(ServiceName)
    ).sort((a, b) =>
        a.ServiceName === 'Adoptions'
            ? 1
            : b.ServiceName === 'Adoptions'
            ? -1
            : 0
    );

    return (
        <div className="store-details-services">
            <TextPassage className="store-details-services__headline" size="lg">
                Services hours & details
            </TextPassage>
            {sortedServices.map(service => {
                const modifiedService = {
                    ...store,
                    StandardStoreHours: service.StandardStoreServiceHours,
                };

                const serviceDisplayTime = getStoreDisplayTime(
                    modifiedService,
                    currentDate
                );
                const isStoreOpen = storeIsOpen(modifiedService, currentDate);

                const serviceUrl = constructServiceURL(service, store) ?? '';

                return (
                    <Link
                        key={service.ServiceName}
                        className="store-details-services__link"
                        href={serviceUrl}
                        onClick={async event =>
                            await handleServiceClick({
                                authType: isAuthenticated,
                                event,
                                link: serviceUrl,
                            })
                        }
                        prefetch={false}
                        target="_blank"
                        tabIndex={0}
                    >
                        <Image
                            src={serviceImages[service.ServiceName] ?? ''}
                            alt={`${service.ServiceName} image`}
                            width={60}
                            height={60}
                        />
                        <div className="store-details-services__info">
                            <TextPassage size="lg">
                                {service.ServiceName === 'Veterinary'
                                    ? 'Veterinary Care'
                                    : service.ServiceName}
                            </TextPassage>
                            <TextPassage
                                className={`store-details-hours__info--${
                                    isStoreOpen ? 'open' : 'closed'
                                }`}
                            >
                                {serviceDisplayTime}
                            </TextPassage>
                        </div>
                        <Icon name="chevron-right" size="lg" />
                    </Link>
                );
            })}
        </div>
    );
}
